import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TimeService } from '@bayclubs/utils';
import { ValidationService } from '../../../../infrastructure/infrastructure.module';
import { campsenum } from '../../../models/enums.generated';
import { camps } from '../../../models/models.generated';

@Injectable()
export class CampsDetailsFormBuilder {
    constructor(
        private formBuilder: UntypedFormBuilder,
        private time: TimeService
    ) {}

    public build(record: camps.CampDefinitionDetails, sessionToExpand: string = null): UntypedFormGroup {
        return this.formBuilder.group({
            basicInfo: this.buildBasicInfo(record),
            visibility: this.buildVisiblity(record),
            pricingDiscounts: this.buildPricingDiscounts(record.discounts, record.id),
            discounts: this.buildDiscounts(record.discounts, record.id),
            sessionGroups: this.buildSessionGroups(record.sessionsInClub, record.id, sessionToExpand),
        });
    }

    buildBasicInfo(record: camps.CampDefinitionDetails): any {
        const basicInfoForm = this.formBuilder.group({
            id: [record.id],
            name: [record.name, Validators.required],
            status: [record.status],
            revenueDepartment: [record.revenueDepartment, Validators.required],
            holdingAccount: [record.holdingAccount, Validators.required],
            category: this.buildCategory(record.category, record.parentCategory),
        });

        if (record.type === campsenum.CampType.Childcare) {
            basicInfoForm.addControl('reservationDaysAheadLimitVariant', this.formBuilder.control(record.reservationDaysAheadLimitVariant, [Validators.required]));

            basicInfoForm.addControl(
                'reservationDaysAheadLimit',
                this.formBuilder.control(record.reservationDaysAheadLimit > 0 ? record.reservationDaysAheadLimit : 1, [Validators.required, Validators.min(1)])
            );
        }

        return basicInfoForm;
    }

    buildVisiblity(record: camps.CampDefinitionDetails): any {
        return this.formBuilder.group({
            id: [record.id],
            description: [record.description],
            isVisibleToMembers: [record.isVisibleToMembers],
            shortDescription: [record.shortDescription],
            displayName: [record.displayName, Validators.required],
            memberWeeklyPrice: [record.memberWeeklyPrice, ValidationService.greaterThanZeroOrEmpty],
            guestWeeklyPrice: [record.guestWeeklyPrice, ValidationService.greaterThanZeroOrEmpty],
        });
    }

    buildCategory(item: camps.CategoryModel, parent: camps.CategoryModel): UntypedFormGroup {
        return this.formBuilder.group({
            id: [item ? item.id : null],
            name: [item ? item.name : null],
            parentId: [parent ? parent.id : null],
            parentName: [parent ? parent.name : null],
        });
    }

    buildSessionGroups(model: camps.SessionsInClub[], campId: string, sessionToExpand: string = null): any {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
            campId: campId,
        });

        const control = <UntypedFormArray>group.controls['items'];

        model.forEach((item) => {
            control.push(this.buildSessionsInClub(item, campId, sessionToExpand));
        });

        return group;
    }

    buildSessionsInClub(model: camps.SessionsInClub, campId: string, sessionToExpand: string = null): UntypedFormGroup {
        let group = this.formBuilder.group({
            active: [false],
            club: [model.clubCode],
            items: this.formBuilder.array([]),
            campId: [campId],
        });

        const control = <UntypedFormArray>group.controls['items'];

        model.sessions.forEach((item) => {
            var sessionInClub = this.buildSessionInClub(item, campId, sessionToExpand);

            control.push(sessionInClub);

            if (sessionInClub.controls['isOpen'].value == true) {
                group.controls['active'].setValue(true);
            }
        });

        return group;
    }

    buildSessionInClub(model: camps.SessionDefinitionDetails, campId: string, sessionToExpand: string = null): UntypedFormGroup {
        var isExpanded = sessionToExpand == model.id;

        let group = this.formBuilder.group({
            id: [model.id],
            isOpen: [isExpanded],
            basicInfo: this.buildSessionBasicInfo(model, campId),
            options: this.buildOptions(model.options, model.id),
            dayVariants: this.buildDayVariants(model.daysVariants, model.id, model.sessionDays),
            // timeVariants: this.buildTimeVariants(model.timeVariants)
            timeVariant: this.buildTimeVariantItem(model.timeVariants[0]),
            pricingMatrix: this.buildPricingMatrix(model.priceMatrix, model.id),
        });

        return group;
    }

    buildTimeVariants(model: camps.TimeRangeVariantDefinitionDetails[]): UntypedFormGroup {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
        });

        const control = <UntypedFormArray>group.controls['items'];

        model.forEach((item) => {
            control.push(this.buildTimeVariantItem(item));
        });

        return group;
    }

    buildTimeVariantItem(model: camps.TimeRangeVariantDefinitionDetails): any {
        let group = this.formBuilder.group({
            id: [model.id],
            basePrice: [model.basePrice],
            timeFromInMinutes: [model.timeFromInMinutes],
            timeToInMinutes: [model.timeToInMinutes],
        });

        return group;
    }

    buildDayVariants(model: camps.DaysVariantDefinitionDetails[], sessionId: string, allDays: camps.SessionDayDefinitionDetails[]): UntypedFormGroup {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
            sessionId: sessionId,
            allDays: [allDays],
        });

        const control = <UntypedFormArray>group.controls['items'];

        model.forEach((item) => {
            control.push(this.buildDayVariantItem(item, sessionId, allDays));
        });

        return group;
    }

    buildPricingMatrix(model: camps.VariantPrice[], sessionId: string): UntypedFormGroup {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
            sessionId: sessionId,
        });

        if (model) {
            const control = <UntypedFormArray>group.controls['items'];

            model.forEach((item) => {
                control.push(
                    this.formBuilder.group({
                        variantId: [item.variantId],
                        variantName: [item.variantName],
                        basePriceTotal: [item.basePriceTotal],
                        tier: [item.tier],
                    })
                );
            });
        }
        return group;
    }

    buildDayVariantItem(model: camps.DaysVariantDefinitionDetails, sessionId: string, allDays: camps.SessionDayDefinitionDetails[]): any {
        let group = this.formBuilder.group({
            id: [model.id],
            name: [model.name, Validators.required],
            order: [model.order, Validators.required],
            isVisibleToMembers: [model.isVisibleToMembers],
            sessionDays: this.formBuilder.array([]),
            daysDates: [model.sessionDays.map((x) => x.date), ValidationService.atLeastOneItemSelected],
            sessionId: sessionId,
            allDays: [allDays.map((x) => x.date)],
        });

        const control = <UntypedFormArray>group.controls['sessionDays'];

        model.sessionDays.forEach((item) => {
            control.push(this.buildSessionDayItem(item));
        });

        return group;
    }

    buildSessionDays(model: camps.SessionDayDefinitionDetails[]): UntypedFormGroup {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
            daysDates: [model.map((x) => x.date), ValidationService.atLeastOneItemSelected],
        });

        const control = <UntypedFormArray>group.controls['items'];

        model.forEach((item) => {
            control.push(this.buildSessionDayItem(item));
        });

        return group;
    }

    buildSessionDayItem(model: camps.SessionDayDefinitionDetails): any {
        return this.formBuilder.group({
            id: [model.id],
            date: [model.date],
            maxCapacity: [model.maxCapacity],
        });
    }

    buildSessionBasicInfo(model: camps.SessionDefinitionDetails, campId: string): UntypedFormGroup {
        return this.formBuilder.group({
            id: [model.id],
            ageRangeFromInMonths: [model.ageRangeFromInMonths, Validators.compose([Validators.required])],
            ageRangeFromInMonthsEffective: [model.ageRangeFromInMonthsEffective, Validators.compose([Validators.required])],
            ageRangeToInMonths: [model.ageRangeToInMonths, Validators.compose([Validators.required])],
            ageRangeToInMonthsEffective: [model.ageRangeToInMonthsEffective, Validators.compose([Validators.required])],
            name: [model.name, Validators.compose([Validators.required])],
            description: [model.description, Validators.compose([Validators.required])],
            clubId: [model.club.id, Validators.compose([Validators.required])],
            clubName: [model.club.name],
            capacity: model.sessionDays.length > 0 ? [model.sessionDays[0].maxCapacity] : 0,
            timeRangeVariant: [model.timeVariants[0].type],
            timeVariantid: [model.timeVariants[0].id],
            basePrice: [model.timeVariants[0].basePrice, Validators.compose([Validators.required])],
            timeFromInMinutes: [model.timeVariants[0].timeFromInMinutes],
            timeToInMinutes: [model.timeVariants[0].timeToInMinutes],
            sessionDays: this.buildSessionDays(model.sessionDays),
            campId: [campId],
            allowCustomVariantSelection: [model.allowCustomVariantSelection],
            allowCampCreditPointsUsage: [model.allowCampCreditPointsUsage],
            contactPhone: [model.contactPhone, Validators.compose([Validators.maxLength(30)])],
            contactEmail: [model.contactEmail, Validators.compose([Validators.maxLength(60)])],
            contactWorkingHours: [model.contactWorkingHours, Validators.compose([Validators.maxLength(200)])],
        });
    }

    buildOptions(model: camps.OptionDefinitionDetails[], sessionId: string): UntypedFormGroup {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
            sessionId: [sessionId],
        });

        const control = <UntypedFormArray>group.controls['items'];

        model.forEach((item) => {
            control.push(this.buildOptionItem(item, sessionId));
        });

        return group;
    }

    buildOptionItem(model: camps.OptionDefinitionDetails, sessionId: string): any {
        return this.formBuilder.group({
            id: [model.id],
            description: [model.description, Validators.required],
            name: [model.name, Validators.required],
            order: [model.order],
            pricePerSession: [model.pricePerSession, Validators.compose([Validators.required, Validators.min(0)])],
            type: [model.type, Validators.required],
            sessionId: [sessionId],
        });
    }

    buildDiscounts(model: camps.DiscountDefinitionDetails[], campId: string): any {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
            campId: [campId],
        });

        const control = <UntypedFormArray>group.controls['items'];

        model
            .filter((x) => x.isCartLevel)
            .forEach((item) => {
                control.push(this.buildDiscountItem(item, campId));
            });

        return group;
    }

    buildPricingDiscounts(model: camps.DiscountDefinitionDetails[], campId: string): any {
        let group = this.formBuilder.group({
            items: this.formBuilder.array([]),
            campId: [campId],
        });

        const control = <UntypedFormArray>group.controls['items'];

        model
            .filter((x) => x.isPricingLevel)
            .forEach((item) => {
                control.push(this.buildDiscountItem(item, campId));
            });

        return group;
    }

    buildDiscountItem(model: camps.DiscountDefinitionDetails, campId: string): UntypedFormGroup {
        return this.formBuilder.group({
            dateFrom: [this.time.toBcDate(model.dateFrom), Validators.required],
            dateTo: [this.time.toBcDate(model.dateTo), Validators.required],
            daysFrom: [model.daysFrom],
            daysTo: [model.daysTo],
            id: [model.id],
            isGlobal: [model.isGlobal],
            membershipTier: [model.membershipTier],
            minimumNumberOfCamps: [model.minimumNumberOfCamps],
            maximumNumberOfCamps: [model.maximumNumberOfCamps],
            minimumNumberOfDays: [model.minimumNumberOfDays],
            maximumNumberOfDays: [model.maximumNumberOfDays],
            name: [model.name, Validators.required],
            type: [model.type, Validators.required],
            valuePercent: [model.valuePercent, Validators.compose([Validators.required, Validators.min(0), Validators.max(100)])],
            campId: campId,
            applicationOrder: [model.applicationOrder, []],
            isPricingLevel: [model.isPricingLevel, []],
            isCartLevel: [model.isCartLevel, []],
        });
    }
}
